import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { AlertTriangle, FlaskConical, Trash2 } from 'lucide-react';

import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { LoadingRows } from '@/components/common/LoadingState';
import { ErrorState } from '@/components/common/ErrorState';
import { inventoryService } from '@/services/inventory';
import { testDataService } from '@/services/platform/test-data';
import { fmt } from '@/lib/format';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Clean test data — dry-run preview + guarded purge. Shows the exact
 * blast radius (test items + every order / dispatch / payment that
 * references them) before the operator confirms. The purge itself is
 * backend-gated on the `test-data-tools` feature flag.
 */
export function CleanTestDataDialog({ open, onOpenChange }: Props) {
  const qc = useQueryClient();

  const previewQuery = useQuery({
    queryKey: ['inventory', 'test-data', 'preview'],
    queryFn: () => inventoryService.previewTestData(),
    enabled: open,
  });

  // Routed through the consolidated, restorable cleanup engine — every
  // row is archived before deletion and can be restored from
  // Settings → Test data → History (replaces the old one-way purge).
  const purge = useMutation({
    mutationFn: () => testDataService.cleanup('Test-data cleanup (Inventory tools)'),
    onSuccess: (res) => {
      toast.success(`Archived + removed ${res.totalRows} record(s) — restorable from Settings → Test data`);
      qc.invalidateQueries({ queryKey: ['inventory'] });
      qc.invalidateQueries({ queryKey: ['test-data'] });
      onOpenChange(false);
    },
    onError: (err: unknown) =>
      toast.error(err instanceof Error ? err.message : 'Cleanup failed'),
  });

  const preview = previewQuery.data;
  const nothingToClean = !!preview && preview.counts.items === 0;

  const countRows: { label: string; value: number }[] = preview
    ? [
        { label: 'Test items', value: preview.counts.items },
        { label: 'Sales orders', value: preview.counts.salesOrders },
        { label: 'Purchase orders', value: preview.counts.purchaseOrders },
        { label: 'Dispatches', value: preview.counts.dispatches },
        { label: 'Payments', value: preview.counts.payments },
        { label: 'Invoices', value: preview.counts.invoices },
        { label: 'Returns', value: preview.counts.returns },
        { label: 'Cancel requests', value: preview.counts.cancelRequests },
        { label: 'Refunds', value: preview.counts.refunds },
      ]
    : [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FlaskConical className="h-4 w-4 text-muted-foreground" />
            Clean test data
          </DialogTitle>
        </DialogHeader>

        {previewQuery.isLoading ? (
          <LoadingRows rows={4} cols={2} />
        ) : previewQuery.error ? (
          <ErrorState error={previewQuery.error} onRetry={() => previewQuery.refetch()} />
        ) : nothingToClean ? (
          <div className="rounded-lg border border-dashed border-border/70 px-4 py-8 text-center">
            <FlaskConical className="mx-auto h-5 w-5 text-muted-foreground/50" />
            <div className="mt-2 text-[12.5px] text-muted-foreground">
              No items are marked as test data. Mark items first — use{' '}
              <span className="font-medium">⋯ → Mark as test data</span> on any
              inventory row.
            </div>
          </div>
        ) : preview ? (
          <div className="space-y-3">
            <div className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/[0.06] px-3 py-2 text-[12px]">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
              <span className="text-foreground">
                This <strong>permanently deletes</strong> everything below.
                Orders, dispatches and payments are removed in full — not
                archived. This cannot be undone.
              </span>
            </div>

            <div className="overflow-hidden rounded-lg border border-border/70">
              <table className="w-full text-[12.5px]">
                <tbody className="divide-y divide-border/60">
                  {countRows.map((row) => (
                    <tr key={row.label}>
                      <td className="px-3 py-1.5 text-muted-foreground">{row.label}</td>
                      <td className="px-3 py-1.5 text-right font-mono font-medium tabular-nums">
                        {fmt.number(row.value)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div>
              <div className="mb-1 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                Test items
              </div>
              <div className="flex flex-wrap gap-1">
                {preview.testItems.map((it) => (
                  <Badge key={it.id} variant="outline" className="font-mono text-[10px]">
                    {it.sku}
                  </Badge>
                ))}
              </div>
            </div>

            {preview.salesOrderNumbers.length > 0 && (
              <div>
                <div className="mb-1 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                  Sales orders to be deleted
                </div>
                <div className="max-h-28 overflow-y-auto rounded-md border border-border/60 bg-muted/20 p-2">
                  <div className="flex flex-wrap gap-1">
                    {preview.salesOrderNumbers.map((n) => (
                      <span key={n} className="font-mono text-[10.5px] text-muted-foreground">
                        {n}
                      </span>
                    ))}
                  </div>
                </div>
                <p className="mt-1 text-[11px] text-muted-foreground">
                  Check this list — anything here is gone for good. If a real
                  order appears, unmark the test item it shares before purging.
                </p>
              </div>
            )}
          </div>
        ) : null}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          {!nothingToClean && (
            <Button
              variant="destructive"
              onClick={() => purge.mutate()}
              disabled={purge.isPending || previewQuery.isLoading || !preview}
            >
              <Trash2 className="h-3.5 w-3.5" />
              {purge.isPending
                ? 'Purging…'
                : preview
                  ? `Purge ${preview.counts.items} item(s) + records`
                  : 'Purge'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
